import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { BarList, Panel, StatTile } from "@/components/analytics-ui";
import { SkAnalyticsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import type { MetaPage } from "@shared/pages/meta";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// Meta -> Audiences: who the ads reach. Age x gender split,
// where they live, and which surface (FB / IG) they're on.

export const Route = createFileRoute("/_app/meta/audiences")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<MetaPage>("meta-audiences", deps));
  },
  component: MetaAudiencesRoute,
});

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n >= 100 ? 0 : 2 });
const num = (n: number) => n.toLocaleString("en-US");

const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
  unknown: "Other",
};

function MetaAudiencesRoute() {
  useTitle("Audiences");
  const search = Route.useSearch();
  return (
    <PageQuery<MetaPage> name="meta-audiences" search={search} skeleton={<SkAnalyticsPage />}>
      {(page) => <MetaAudiences page={page} />}
    </PageQuery>
  );
}

function MetaAudiences({ page }: { page: MetaPage }) {
  const { configured, report: live, presets } = page;

  // No sample data, ever: absent feed or a hidden section renders a plain
  // explanation panel (the sidebar hides this page in that case anyway).
  if (!configured || !live || !live.ads?.demographics?.length) {
    return (
      <div className="page-shell">
        <PageHeader title="Audiences" />
        <Panel title={!configured || !live ? "Not connected" : "Not enabled"} className="mt-5">
          <p className="text-sm text-muted">
            {!configured || !live
              ? "Meta numbers show here once Digital King connects the Meta account."
              : "Demographic breakdowns are enabled per account by the agency. They will appear here automatically once switched on in the agency hub."}
          </p>
        </Panel>
      </div>
    );
  }
  const ads = live.ads;
  const demographics = ads.demographics ?? [];
  const regions = ads.regions ?? [];
  const platforms = ads.platforms ?? [];
  const showSpend = demographics.some((d) => d.spend !== undefined);

  // Age x gender pyramid data: group by age bracket.
  const AGES = [...new Set(demographics.map((d) => d.age))].sort();
  const byAge = AGES.map((age) => {
    const rows = demographics.filter((d) => d.age === age);
    const val = (g: string) => {
      const r = rows.find((x) => x.gender === g);
      return r ? (showSpend ? Math.round(r.spend ?? 0) : r.clicks) : 0;
    };
    return { age, female: val("female"), male: val("male"), unknown: val("unknown") };
  });
  const maxSide = Math.max(1, ...byAge.flatMap((a) => [a.female, a.male + a.unknown]));

  const totFemale = byAge.reduce((s, a) => s + a.female, 0);
  const totMale = byAge.reduce((s, a) => s + a.male + a.unknown, 0);
  const total = Math.max(1, totFemale + totMale);
  const topAge = [...byAge].sort((a, b) => b.female + b.male + b.unknown - (a.female + a.male + a.unknown))[0];
  const topRegion = regions[0];
  const unit = showSpend ? "$ spend" : "clicks";
  const fmtVal = (n: number) => (showSpend ? usd(n) : num(n));

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Audiences" info={`Who your Meta ads reach, by ${unit}.`} />
        <DateRangeFilter presets={presets} defaultKey="30d" />
      </div>

      <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
        <StatTile label="Women" value={`${Math.round((totFemale / total) * 100)}%`} sub={`of ${unit}`} />
        <StatTile label="Men" value={`${Math.round((totMale / total) * 100)}%`} sub={`of ${unit}`} />
        <StatTile label="Top age group" value={topAge?.age ?? "–"} sub={topAge ? fmtVal(topAge.female + topAge.male + topAge.unknown) : undefined} />
        <StatTile label="Top region" value={topRegion?.label ?? "–"} sub={topRegion ? fmtVal(showSpend ? Math.round(topRegion.spend ?? 0) : topRegion.clicks) : undefined} />
      </div>

      <Panel title={`Age & gender (${unit})`} className="mt-4">
        <div className="space-y-2.5">
          <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
            <span className="flex-1 text-right">Women</span>
            <span className="w-14 text-center">Age</span>
            <span className="flex-1">Men</span>
          </div>
          {byAge.map((a) => (
            <div key={a.age} className="flex items-center gap-3">
              <div className="flex flex-1 justify-end">
                <div className="group/bar relative h-5 rounded-l-md bg-primary/80" style={{ width: `${(a.female / maxSide) * 100}%`, minWidth: a.female ? 3 : 0 }}>
                  <span className="pointer-events-none absolute right-1.5 top-1/2 hidden -translate-y-1/2 text-[10px] font-semibold text-primary-foreground group-hover/bar:inline">
                    {fmtVal(a.female)}
                  </span>
                </div>
              </div>
              <span className="w-14 shrink-0 text-center text-xs tabular-nums text-muted">{a.age}</span>
              <div className="flex flex-1">
                <div className="group/bar relative h-5 rounded-r-md bg-accent" style={{ width: `${((a.male + a.unknown) / maxSide) * 100}%`, minWidth: a.male + a.unknown ? 3 : 0 }}>
                  <span className="pointer-events-none absolute left-1.5 top-1/2 hidden -translate-y-1/2 text-[10px] font-semibold text-accent-foreground group-hover/bar:inline">
                    {fmtVal(a.male + a.unknown)}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {byAge.length === 0 && <p className="py-4 text-center text-sm text-muted">No demographic data in this range.</p>}
        </div>
        <div className="mt-3 flex gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-primary/80" /> Women · {fmtVal(totFemale)}</span>
          <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-accent" /> Men · {fmtVal(totMale)}</span>
        </div>
      </Panel>

      <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
        <Panel title={`Regions (${unit})`}>
          <BarList
            toneClass="bg-primary"
            items={regions.map((r) => ({
              label: r.label,
              count: showSpend ? Math.round(r.spend ?? 0) : r.clicks,
              sub: `${num(r.clicks)} clicks · ${num(r.impressions)} impr.`,
            }))}
            emptyText="No region data in this range."
          />
        </Panel>
        <Panel title={`Platforms (${unit})`}>
          <BarList
            items={platforms.map((p) => ({
              label: PLATFORM_LABELS[p.label] ?? p.label,
              count: p.spend !== undefined ? Math.round(p.spend) : p.clicks,
              sub: `${num(p.clicks)} clicks · ${num(p.impressions)} impr.`,
            }))}
            emptyText="No platform data in this range."
          />
        </Panel>
      </div>
    </div>
  );
}
